import { GitMerge, Layers, ListTree, PanelsTopLeft, Rocket, Sigma, Table2, Zap, type LucideIcon } from 'lucide-react';
import type { LocalizedText } from '@/types';
import type { ApiProblemTopic } from '@/api/types';

/**
 * Prezentatsiya metadatasi (ikonka + 3 tilli sarlavha).
 * Sonlar API dan keladi — bu yerda to'qima count yo'q.
 * slug = backend kanonik topic (JOIN, WINDOW, ...).
 */
export interface TopicMeta {
  slug: ApiProblemTopic;
  title: LocalizedText;
  /** Mavzu sahifasi (/topics/:topic) uchun qisqa tavsif */
  desc: LocalizedText;
  icon: LucideIcon;
}

export const TOPICS: TopicMeta[] = [
  {
    slug: 'SELECT',
    icon: Table2,
    title: { uz: 'SELECT asoslari', ru: 'Основы SELECT', en: 'SELECT Basics' },
    desc: {
      uz: "SELECT, WHERE, ORDER BY, LIMIT va DISTINCT bilan ma'lumotni tanlash va filtrlash bo'yicha SQL masalalar.",
      ru: 'Задачи по SQL на выборку и фильтрацию данных: SELECT, WHERE, ORDER BY, LIMIT и DISTINCT.',
      en: 'SQL problems on selecting and filtering data with SELECT, WHERE, ORDER BY, LIMIT and DISTINCT.',
    },
  },
  {
    slug: 'JOIN',
    icon: GitMerge,
    title: { uz: "JOIN'lar", ru: 'JOIN', en: 'JOINs' },
    desc: {
      uz: "INNER, LEFT, RIGHT, FULL va SELF JOIN yordamida bir nechta jadvalni birlashtirish bo'yicha SQL masalalar.",
      ru: 'Задачи по SQL на объединение таблиц: INNER, LEFT, RIGHT, FULL и SELF JOIN.',
      en: 'SQL problems on combining tables with INNER, LEFT, RIGHT, FULL and SELF JOINs.',
    },
  },
  {
    slug: 'AGGREGATE',
    icon: Sigma,
    title: { uz: 'Agregatsiyalar', ru: 'Агрегации', en: 'Aggregations' },
    desc: {
      uz: "COUNT, SUM, AVG, GROUP BY va HAVING bilan ma'lumotni guruhlash va hisoblash bo'yicha SQL masalalar.",
      ru: 'Задачи по SQL на группировку и агрегацию: COUNT, SUM, AVG, GROUP BY и HAVING.',
      en: 'SQL problems on grouping and aggregation with COUNT, SUM, AVG, GROUP BY and HAVING.',
    },
  },
  {
    slug: 'SUBQUERY',
    icon: Layers,
    title: { uz: "Subquery'lar", ru: 'Подзапросы', en: 'Subqueries' },
    desc: {
      uz: "Ichma-ich so'rovlar, korrelyatsiyalangan subquery, IN va EXISTS bo'yicha SQL masalalar.",
      ru: 'Задачи по SQL на вложенные и коррелированные подзапросы, IN и EXISTS.',
      en: 'SQL problems on nested and correlated subqueries, IN and EXISTS.',
    },
  },
  {
    slug: 'WINDOW',
    icon: PanelsTopLeft,
    title: { uz: 'Window funksiyalar', ru: 'Оконные функции', en: 'Window Functions' },
    desc: {
      uz: "ROW_NUMBER, RANK, LAG, LEAD va OVER (PARTITION BY ...) kabi oyna funksiyalari bo'yicha SQL masalalar.",
      ru: 'Задачи по SQL на оконные функции: ROW_NUMBER, RANK, LAG, LEAD и OVER (PARTITION BY ...).',
      en: 'SQL problems on window functions such as ROW_NUMBER, RANK, LAG, LEAD and OVER (PARTITION BY ...).',
    },
  },
  {
    slug: 'CTE',
    icon: ListTree,
    title: { uz: 'CTE & Recursive', ru: 'CTE и рекурсия', en: 'CTE & Recursive' },
    desc: {
      uz: "WITH (CTE) va WITH RECURSIVE yordamida murakkab so'rovlarni bosqichma-bosqich yozish bo'yicha SQL masalalar.",
      ru: 'Задачи по SQL на общие табличные выражения (WITH) и рекурсивные запросы (WITH RECURSIVE).',
      en: 'SQL problems on common table expressions (WITH) and recursive queries (WITH RECURSIVE).',
    },
  },
  {
    slug: 'INDEX',
    icon: Zap,
    title: { uz: 'Indexing & Perf', ru: 'Индексы и производительность', en: 'Indexing & Perf' },
    desc: {
      uz: "Indekslar va so'rov samaradorligi bo'yicha SQL masalalar.",
      ru: 'Задачи по SQL на индексы и производительность запросов.',
      en: 'SQL problems on indexes and query performance.',
    },
  },
  {
    slug: 'ADVANCED',
    icon: Rocket,
    title: { uz: 'Advanced', ru: 'Продвинутый', en: 'Advanced' },
    desc: {
      uz: "Bir nechta texnikani birlashtiradigan murakkab SQL masalalar.",
      ru: 'Сложные задачи по SQL, сочетающие несколько техник.',
      en: 'Advanced SQL problems that combine several techniques.',
    },
  },
];

/** Mavzu sahifasi URL: JOIN → /topics/join */
export const topicPath = (slug: string): string => `/topics/${slug.toLowerCase()}`;

/** URL parametridan (join, JOIN) mavzuni topadi. */
export const topicFromParam = (param: string | undefined): TopicMeta | undefined =>
  param ? TOPICS.find((t) => t.slug.toLowerCase() === param.toLowerCase()) : undefined;

/** Eski bookmarklar: joins → JOIN */
const LEGACY_TOPIC_ALIAS: Record<string, ApiProblemTopic> = {
  select: 'SELECT',
  joins: 'JOIN',
  join: 'JOIN',
  aggregate: 'AGGREGATE',
  subquery: 'SUBQUERY',
  window: 'WINDOW',
  cte: 'CTE',
  index: 'INDEX',
  advanced: 'ADVANCED',
};

export function normalizeTopicParam(raw: string | null | undefined): ApiProblemTopic | 'all' {
  if (!raw || raw === 'all') return 'all';
  const upper = raw.trim().toUpperCase();
  if (TOPICS.some((t) => t.slug === upper)) {
    return upper as ApiProblemTopic;
  }
  const alias = LEGACY_TOPIC_ALIAS[raw.trim().toLowerCase()];
  return alias ?? 'all';
}

export const topicBySlug = (slug: string): TopicMeta | undefined =>
  TOPICS.find((t) => t.slug === slug || t.slug === slug.toUpperCase());

/**
 * Asosiy mavzu tanlash tartibi: masala o'rgatadigan eng "maxsus" tushuncha. API dagi
 * topics tartibiga bog'liq emas — breadcrumb, JSON-LD, "o'xshash masalalar" va OG rasm
 * hammasi shu funksiyadan foydalanadi (bir masala — bitta mavzu).
 */
const PRIMARY_TOPIC_PRIORITY: ApiProblemTopic[] = [
  'WINDOW',
  'CTE',
  'SUBQUERY',
  'JOIN',
  'AGGREGATE',
  'INDEX',
  'ADVANCED',
  'SELECT',
];

export function primaryTopic(topics: readonly string[] | null | undefined): TopicMeta | undefined {
  if (!topics?.length) return undefined;
  const set = new Set(topics.map((t) => t.toUpperCase()));
  const slug = PRIMARY_TOPIC_PRIORITY.find((t) => set.has(t));
  return slug ? topicBySlug(slug) : undefined;
}
